import { CircularProgress, Skeleton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useExtensionConfigs } from "../../../hooks/useExtensionConfigs";
import { useInstalledExtensions } from "../../../hooks/useInstalledExtensions";
import useFetch from "../../../hooks/useFetch";
import { IExtensionConfig } from "../../../models/extensions";
import { Extension } from "../../../models/intalled-extensions";
import { INavigationItem } from "../../../models/navigation";
import { Domains } from "../../../models/permission";
import { getClickRepasseAuthUrl } from "../../../services/clickRepasse.service";
import { tokenService } from "../../../services/token.service";
import { tokenTemporaryService } from "../../../services/tokenTemorary.service";
import { useAppSelector } from "../../../store/store";
import useSiteApp from "../../../hooks/useSiteApp";
import { IActionLabel } from "../../../models/theme";
import { NavigationIcon } from "./NavigationIcon/NavigationIcon";

const translationPath = "page.home.";

export const useNavigationItems = () => {
  const {
    t,
    i18n: { language: lang },
  } = useTranslation();

  const { fullName, individual } = useAppSelector((state) => state.user);

  const sideMenu = useAppSelector((state) => state.theme.sideMenu);

  const temporaryName = tokenTemporaryService.getTemporaryAccessName();

  const isLogged = !!tokenService.getAccessToken();

  const [faqInstalled] = useInstalledExtensions(Extension.FAQ);

  const [externalCalendarButtonInstalled] = useInstalledExtensions(
    Extension.EXTERNAL_CALENDAR_BUTTON,
  );

  const [refundInstalled] = useInstalledExtensions(Extension.REFUND);

  const [irInstalled] = useInstalledExtensions(Extension.IRPF);

  const [ticketInstalled] = useInstalledExtensions(Extension.PAYMENTSLIP);

  const [examsInstalled] = useInstalledExtensions(Extension.EXAMS);

  const [managementExamsInstalled] = useInstalledExtensions(
    Extension.MANAGEMENT_EXAMS,
  );
  
  const [whatsappInstalled] = useInstalledExtensions(Extension.WHATSAPP);

  const [coparticipationInstalled] = useInstalledExtensions(
    Extension.COPARTICIPATION,
  );

  const [authorizationsInstalled] = useInstalledExtensions(
    Extension.AUTHORIZATIONS,
  );

  const [healthProgramInstalled] = useInstalledExtensions(
    Extension.HEALTH_PROGRAM,
  );

  const [graceperiodInstalled] = useInstalledExtensions(
    Extension.GRACE_PERIOD_CPT,
  );

  const [medicalTransferInstalled] = useInstalledExtensions(
    Extension.MEDICAL_TRANSFER,
  );

  const [doctorAgendaInstalled] = useInstalledExtensions(
    Extension.DOCTOR_AGENDA,
  );

  const [actionLabelText, setActionLabelText] = useState<IExtensionConfig>();

  const [actionLabelIcon, setActionLabelIcon] = useState<IExtensionConfig>();

  const [medicalTransferActionLabelText, setMedicalTransferActionLabelText] =
    useState<IExtensionConfig>();

  const [medicalTransferActionLabelIcon, setMedicalTransferActionLabelIcon] =
    useState<IExtensionConfig>();

  const { sendRequest: sendClickRepasseAuthRequest, loading: clickRepasseLoading } =
    useFetch(getClickRepasseAuthUrl);

  const openClickRepasse = useCallback(async () => {
    const { data, success } = await sendClickRepasseAuthRequest(null);
    if (data && success) {
      window.open(data, "_blank");
    }
  }, [sendClickRepasseAuthRequest]);

  const [open, setOpen] = useState(false);

  const onOpenHandler = useCallback(() => {
    setOpen(true);
  }, []);

  const onCloseHandler = useCallback(() => {
    setOpen(false);
  }, []);

  const { fetchExtensionConfig, loadingExtensionConfigs } =
    useExtensionConfigs();

  const fetchExtensions = useCallback(async () => {
    const configActionLabelText = await fetchExtensionConfig(
      Extension.EXTERNAL_CALENDAR_BUTTON,
      "ActionLabelText",
    );
    setActionLabelText(configActionLabelText);

    const configActionLabelIcon = await fetchExtensionConfig(
      Extension.EXTERNAL_CALENDAR_BUTTON,
      "ActionLabelIcon",
    );
    setActionLabelIcon(configActionLabelIcon);

    const configmedicalTransferactionLabelIcon = await fetchExtensionConfig(
      Extension.MEDICAL_TRANSFER,
      "ActionLabelIcon",
    );
    setMedicalTransferActionLabelIcon(configmedicalTransferactionLabelIcon);
    const configmedicalTransferactionLabelText = await fetchExtensionConfig(
      Extension.MEDICAL_TRANSFER,
      "ActionLabelText",
    );
    setMedicalTransferActionLabelText(configmedicalTransferactionLabelText);
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  const [scheduleInstalled] = useInstalledExtensions(Extension.SCHEDULE);

  const [newScheduleInstalled] = useInstalledExtensions(Extension.NEW_SCHEDULE);

  const [teleconsultation] = useInstalledExtensions(Extension.EMERGENCY_CARE);

  const [phone] = useInstalledExtensions(Extension.TELEPHONE);

  const [topMed] = useInstalledExtensions(Extension.TOPMED_SCHEDULE);

  const [triageInstalled] = useInstalledExtensions(Extension.TRIAGE);

  const { config } = useSiteApp();

  const getActionConfig = useCallback(
    (link: string) => {
      if (!config.actions || config.actions.length === 0) return null;

      const action = config.actions.find((action) => action.link === link);
      if (!action) return null;

      return action;
    },
    [config],
  );

  const getActionName = useCallback(
    (defaultLabel: string, label?: IActionLabel) => {
      return label
        ? label[lang as keyof IActionLabel] || defaultLabel
        : defaultLabel;
    },
    [lang],
  );

  const hasUser = useMemo(
    () => fullName || temporaryName,
    [fullName, temporaryName],
  );

  const scheduleActionConfig = getActionConfig("/schedule");
  const newScheduleActionConfig = getActionConfig("/schedule-new");
  const externalCalendarButtonConfig = getActionConfig(
    "/externalCalendarButton",
  );
  const scheduleTeleconsultationConfig = getActionConfig(
    "/schedule-teleconsultation",
  );
  const triageConfig = getActionConfig("/triage");
  const whatsappConfig = getActionConfig("/whats");
  const examsConfig = getActionConfig("/exams");
  const managementLaboratorialExamsConfig = getActionConfig(
    "/management-laboratorial-exams",
  );
  const healthProgramsConfig = getActionConfig("/health-programs");
  const ticketConfig = getActionConfig("/history-payment");
  const authorizationsConfig = getActionConfig("/authorizations");
  const coparticipationConfig = getActionConfig("/extract-co-participation");
  const irConfig = getActionConfig("/irpf");
  const refundConfig = getActionConfig("/refunds");
  const faqConfig = getActionConfig("/faq");
  const graceperiodConfig = getActionConfig("/grace-period-cpt");
  const doctorAgenda = getActionConfig("/doctor-agenda");
  const medicalTransferConfig = getActionConfig("/medical-transfer");
  const hasExamManagerProfile = individual.profiles.some(
    (profile) => profile.code === "9" || profile.code === "10" || profile.code === "11",
  );

  const items: INavigationItem[] = useMemo(
    () => [
      {
        name: loadingExtensionConfigs
          ? ""
          : getActionName(
              t(translationPath + actionLabelText?.value),
              externalCalendarButtonConfig && externalCalendarButtonConfig.label
                ? externalCalendarButtonConfig.label
                : undefined,
            ),
        link: "/externalCalendarButton",
        icon: loadingExtensionConfigs ? (
          <Skeleton sx={{ width: 60, height: 100 }} />
        ) : (
          <NavigationIcon
            icon={
              externalCalendarButtonConfig && externalCalendarButtonConfig.icon
                ? externalCalendarButtonConfig.icon
                : undefined
            }
            iconColor={
              externalCalendarButtonConfig
                ? externalCalendarButtonConfig.iconColor
                : undefined
            }
            defaultIcon={actionLabelIcon?.value || ""}
            alt={
              externalCalendarButtonConfig && externalCalendarButtonConfig.label
                ? externalCalendarButtonConfig.label[
                    lang as keyof IActionLabel
                  ] ||
                  t(translationPath + actionLabelText?.value) ||
                  "Schedule App"
                : t(translationPath + actionLabelText?.value) || "Schedule App"
            }
          />
        ),
        isExtensionInstalled: externalCalendarButtonInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "schedule"),
          scheduleActionConfig && scheduleActionConfig.label
            ? scheduleActionConfig.label
            : undefined,
        ),
        link: "/schedule",
        icon: (
          <NavigationIcon
            icon={
              scheduleActionConfig && scheduleActionConfig.icon
                ? scheduleActionConfig.icon
                : undefined
            }
            iconColor={
              scheduleActionConfig ? scheduleActionConfig.iconColor : undefined
            }
            defaultIcon="/actions/default/schedule.png"
            alt={
              scheduleActionConfig && scheduleActionConfig.label
                ? scheduleActionConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "schedule") ||
                  "Schedule"
                : t(translationPath + "schedule") || "Schedule"
            }
          />
        ),
        isExtensionInstalled: scheduleInstalled,
        onlyLogged: false,
        onClick: !hasUser ? onOpenHandler : undefined,
      },
      {
        name: getActionName(
          t(translationPath + "schedule"),
          newScheduleActionConfig && newScheduleActionConfig.label
            ? newScheduleActionConfig.label
            : undefined,
        ),
        link: "/schedule-new",
        icon: (
          <NavigationIcon
            icon={
              newScheduleActionConfig && newScheduleActionConfig.icon
                ? newScheduleActionConfig.icon
                : undefined
            }
            iconColor={
              newScheduleActionConfig
                ? newScheduleActionConfig.iconColor
                : undefined
            }
            defaultIcon="/actions/default/schedule-add.png"
            alt={
              newScheduleActionConfig && newScheduleActionConfig.label
                ? newScheduleActionConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "schedule") ||
                  "Schedule"
                : t(translationPath + "schedule") || "Schedule"
            }
          />
        ),
        isExtensionInstalled: newScheduleInstalled,
        onlyLogged: false,
        onClick: !hasUser ? onOpenHandler : undefined,
      },
      {
        name: getActionName(
          t(translationPath + "schedule_teleconsultation"),
          scheduleTeleconsultationConfig && scheduleTeleconsultationConfig.label
            ? scheduleTeleconsultationConfig.label
            : undefined,
        ),
        link: "/schedule-teleconsultation",
        icon: (
          <NavigationIcon
            icon={
              scheduleTeleconsultationConfig &&
              scheduleTeleconsultationConfig.icon
                ? scheduleTeleconsultationConfig.icon
                : undefined
            }
            iconColor={
              scheduleTeleconsultationConfig
                ? scheduleTeleconsultationConfig.iconColor
                : undefined
            }
            defaultIcon={"/actions/default/virtual-emergency-care.png"}
            alt={
              scheduleTeleconsultationConfig &&
              scheduleTeleconsultationConfig.label
                ? scheduleTeleconsultationConfig.label[
                    lang as keyof IActionLabel
                  ] ||
                  t(translationPath + "schedule_teleconsultation") ||
                  "Schedule a teleconsultation"
                : t(translationPath + "schedule_teleconsultation") ||
                  "Schedule a teleconsultation"
            }
          />
        ),
        isExtensionInstalled: teleconsultation || topMed || phone,
        onlyLogged: false,
        onClick: !hasUser ? onOpenHandler : undefined,
      },
      {
        name: getActionName(
          t(translationPath + "triage"),
          triageConfig && triageConfig.label ? triageConfig.label : undefined,
        ),
        link: "/triage",
        icon: (
          <NavigationIcon
            icon={
              triageConfig && triageConfig.icon ? triageConfig.icon : undefined
            }
            iconColor={triageConfig ? triageConfig.iconColor : undefined}
            defaultIcon={"/actions/default/triage.png"}
            alt={
              triageConfig && triageConfig.label
                ? triageConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "triage") ||
                  "Triage"
                : t(translationPath + "triage") || "Triage"
            }
          />
        ),
        isExtensionInstalled: triageInstalled,
        onlyLogged: false,
        onClick: !hasUser ? onOpenHandler : undefined,
      },
      {
        name: getActionName(
          "Whatsapp",
          whatsappConfig && whatsappConfig.label
            ? whatsappConfig.label
            : undefined,
        ),
        link: "/whats",
        icon: (
          <NavigationIcon
            icon={
              whatsappConfig && whatsappConfig.icon
                ? whatsappConfig.icon
                : undefined
            }
            iconColor={whatsappConfig ? whatsappConfig.iconColor : undefined}
            defaultIcon={"/actions/default/whatsapp.png"}
            alt={
              whatsappConfig && whatsappConfig.label
                ? whatsappConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "health_program") ||
                  "Health Program"
                : t(translationPath + "health_program") || "Health Program"
            }
          />
        ),
        isExtensionInstalled: !sideMenu && whatsappInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "doctor_agenda"),
          doctorAgenda && doctorAgenda.label ? doctorAgenda.label : undefined,
        ),
        link: "/doctor-agenda",
        icon: (
          <NavigationIcon
            icon={
              doctorAgenda && doctorAgenda.icon ? doctorAgenda.icon : undefined
            }
            iconColor={doctorAgenda ? doctorAgenda.iconColor : undefined}
            defaultIcon={"/actions/default/doctor-calendar.png"}
            alt={
              doctorAgenda && doctorAgenda.label
                ? doctorAgenda.label[lang as keyof IActionLabel] ||
                  t(translationPath + "doctor_agenda") ||
                  "Doctor's Agenda"
                : t(translationPath + "doctor_agenda") || "Doctor's Agenda"
            }
          />
        ),
        isExtensionInstalled: doctorAgendaInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "exams"),
          examsConfig && examsConfig.label ? examsConfig.label : undefined,
        ),
        link: hasUser ? "/exams" : "/login/exams",
        cardValidatorLink: "/exams",
        icon: (
          <NavigationIcon
            icon={
              examsConfig && examsConfig.icon ? examsConfig.icon : undefined
            }
            iconColor={examsConfig ? examsConfig.iconColor : undefined}
            defaultIcon={"/actions/default/exam-lab.png"}
            alt={
              examsConfig && examsConfig.label
                ? examsConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "exams") ||
                  "Exams"
                : t(translationPath + "exams") || "Exams"
            }
          />
        ),
        domain: isLogged ? Domains.EXAMS : undefined,
        isExtensionInstalled: examsInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "management_exams"),
          managementLaboratorialExamsConfig &&
            managementLaboratorialExamsConfig.label
            ? managementLaboratorialExamsConfig.label
            : undefined,
        ),
        link: "/management-laboratorial-exams",
        icon: (
          <NavigationIcon
            icon={
              managementLaboratorialExamsConfig &&
              managementLaboratorialExamsConfig.icon
                ? managementLaboratorialExamsConfig.icon
                : undefined
            }
            iconColor={
              managementLaboratorialExamsConfig
                ? managementLaboratorialExamsConfig.iconColor
                : undefined
            }
            defaultIcon={"/actions/default/exams-management.png"}
            alt={
              managementLaboratorialExamsConfig &&
              managementLaboratorialExamsConfig.label
                ? managementLaboratorialExamsConfig.label[
                    lang as keyof IActionLabel
                  ] ||
                  t(translationPath + "management_exams") ||
                  "Exam management"
                : t(translationPath + "management_exams") || "Exam management"
            }
          />
        ),
        domain: Domains.EXAMS,
        onlyLogged: true,
        isExtensionInstalled: managementExamsInstalled && hasExamManagerProfile,
      },
      {
        name: getActionName(
          t(translationPath + "health_program"),
          healthProgramsConfig && healthProgramsConfig.label
            ? healthProgramsConfig.label
            : undefined,
        ),
        link: "/health-programs",
        icon: (
          <NavigationIcon
            icon={
              healthProgramsConfig && healthProgramsConfig.icon
                ? healthProgramsConfig.icon
                : undefined
            }
            iconColor={
              healthProgramsConfig ? healthProgramsConfig.iconColor : undefined
            }
            defaultIcon={"/actions/default/healthprogram.png"}
            alt={
              healthProgramsConfig && healthProgramsConfig.label
                ? healthProgramsConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "health_program") ||
                  "Health Program"
                : t(translationPath + "health_program") || "Health Program"
            }
          />
        ),
        isExtensionInstalled: healthProgramInstalled,
        domain: isLogged ? Domains.HEALTH_PROGRAM : undefined,
      },
      {
        name: getActionName(
          t(translationPath + "ticket"),
          ticketConfig && ticketConfig.label ? ticketConfig.label : undefined,
        ),
        link: "/history-payment",
        icon: (
          <NavigationIcon
            icon={
              ticketConfig && ticketConfig.icon ? ticketConfig.icon : undefined
            }
            iconColor={ticketConfig ? ticketConfig.iconColor : undefined}
            defaultIcon={"/actions/default/money.png"}
            alt={
              ticketConfig && ticketConfig.label
                ? ticketConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "ticket") ||
                  "Duplicate ticket"
                : t(translationPath + "ticket") || "Duplicate ticket"
            }
          />
        ),
        domain: Domains.PAYMENTSLIP,
        isExtensionInstalled: ticketInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "authorization"),
          ticketConfig && ticketConfig.label ? ticketConfig.label : undefined,
        ),
        link: "/authorizations",
        icon: (
          <NavigationIcon
            icon={
              authorizationsConfig && authorizationsConfig.icon
                ? authorizationsConfig.icon
                : undefined
            }
            iconColor={
              authorizationsConfig ? authorizationsConfig.iconColor : undefined
            }
            defaultIcon={"/actions/default/authorization.png"}
            alt={
              authorizationsConfig && authorizationsConfig.label
                ? authorizationsConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "coParticipation") ||
                  "Authorizations"
                : t(translationPath + "coParticipation") || "Authorizations"
            }
          />
        ),
        domain: Domains.AUTH,
        isExtensionInstalled: authorizationsInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "coParticipation"),
          coparticipationConfig && coparticipationConfig.label
            ? coparticipationConfig.label
            : undefined,
        ),
        link: "/extract-co-participation",
        icon: (
          <NavigationIcon
            icon={
              coparticipationConfig && coparticipationConfig.icon
                ? coparticipationConfig.icon
                : undefined
            }
            iconColor={
              coparticipationConfig
                ? coparticipationConfig.iconColor
                : undefined
            }
            defaultIcon={"/actions/default/coparticipacao.png"}
            alt={
              coparticipationConfig && coparticipationConfig.label
                ? coparticipationConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "coParticipation") ||
                  "Co-participation extract"
                : t(translationPath + "coParticipation") ||
                  "Co-participation extract"
            }
          />
        ),
        domain: Domains.COPARTICIPATION,
        isExtensionInstalled: coparticipationInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "irAndReimbursement"),
          irConfig && irConfig.label ? irConfig.label : undefined,
        ),
        link: "/irpf",
        icon: (
          <NavigationIcon
            icon={irConfig && irConfig.icon ? irConfig.icon : undefined}
            iconColor={irConfig ? irConfig.iconColor : undefined}
            defaultIcon={"/actions/default/ir.png"}
            alt={
              irConfig && irConfig.label
                ? irConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "irAndReimbursement") ||
                  "IR and Refund"
                : t(translationPath + "irAndReimbursement") || "IR and Refund"
            }
          />
        ),
        domain: Domains.IRPF,
        isExtensionInstalled: irInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "refunds"),
          refundConfig && refundConfig.label ? refundConfig.label : undefined,
        ),
        link: "/refunds",
        icon: (
          <NavigationIcon
            icon={
              refundConfig && refundConfig.icon ? refundConfig.icon : undefined
            }
            iconColor={refundConfig ? refundConfig.iconColor : undefined}
            defaultIcon={"/actions/default/refund.png"}
            alt={
              refundConfig && refundConfig.label
                ? refundConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "refunds") ||
                  "Refunds"
                : t(translationPath + "refunds") || "Refunds"
            }
          />
        ),
        domain: Domains.REFUND,
        isExtensionInstalled: refundInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "faq"),
          faqConfig && faqConfig.label ? faqConfig.label : undefined,
        ),
        link: "/faq",
        icon: (
          <NavigationIcon
            icon={faqConfig && faqConfig.icon ? faqConfig.icon : undefined}
            iconColor={faqConfig ? faqConfig.iconColor : undefined}
            defaultIcon={"/actions/default/faq.png"}
            alt={
              faqConfig && faqConfig.label
                ? faqConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "refunds") ||
                  "Refunds"
                : t(translationPath + "refunds") || "Refunds"
            }
          />
        ),
        isExtensionInstalled: faqInstalled,
      },
      {
        name: getActionName(
          t(translationPath + "grace_period"),
          graceperiodConfig && graceperiodConfig.label
            ? graceperiodConfig.label
            : undefined,
        ),
        link: "/grace-period-cpt",
        icon: (
          <NavigationIcon
            icon={
              graceperiodConfig && graceperiodConfig.icon
                ? graceperiodConfig.icon
                : undefined
            }
            iconColor={
              graceperiodConfig ? graceperiodConfig.iconColor : undefined
            }
            defaultIcon={"/actions/default/grace.png"}
            alt={
              graceperiodConfig && graceperiodConfig.label
                ? graceperiodConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "grace_period") ||
                  "Grace period and CPT"
                : t(translationPath + "grace_period") || "Grace period and CPT"
            }
          />
        ),
        onlyLogged: false,
        domain: isLogged ? Domains.GRACE_PERIOD_CPT : undefined,
        isExtensionInstalled: graceperiodInstalled,
        onClick: !hasUser ? onOpenHandler : undefined,
      },
      {
        name: getActionName(
          t(translationPath + medicalTransferActionLabelText?.value),
          medicalTransferConfig && medicalTransferConfig.label
            ? medicalTransferConfig.label
            : undefined,
        ),
        cardValidatorLink: "/medical-transfer",
        link: "#",
        icon: clickRepasseLoading ? (
          <CircularProgress size={60} />
        ) : (
          <NavigationIcon
            icon={
              medicalTransferConfig && medicalTransferConfig.icon
                ? medicalTransferConfig.icon
                : undefined
            }
            iconColor={
              medicalTransferConfig
                ? medicalTransferConfig.iconColor
                : undefined
            }
            defaultIcon={medicalTransferActionLabelIcon?.value || ""}
            alt={
              medicalTransferConfig && medicalTransferConfig.label
                ? medicalTransferConfig.label[lang as keyof IActionLabel] ||
                  t(translationPath + "medical_transfer") ||
                  "Medical Transfer"
                : t(translationPath + "medical_transfer") || "Medical Transfer"
            }
          />
        ),
        onlyLogged: true,
        isExtensionInstalled: medicalTransferInstalled,
        onClick: openClickRepasse,
      },
    ],
    [
      t,
      faqInstalled,
      refundInstalled,
      irInstalled,
      whatsappInstalled,
      sideMenu,
      coparticipationInstalled,
      authorizationsInstalled,
      ticketInstalled,
      healthProgramInstalled,
      isLogged,
      examsInstalled,
      managementExamsInstalled,
      hasUser,
      scheduleInstalled,
      newScheduleInstalled,
      externalCalendarButtonInstalled,
      actionLabelText,
      actionLabelIcon,
      triageInstalled,
      loadingExtensionConfigs,
      teleconsultation,
      graceperiodInstalled,
      lang,
      authorizationsConfig,
      externalCalendarButtonConfig,
      scheduleActionConfig,
      newScheduleActionConfig,
      scheduleTeleconsultationConfig,
      triageConfig,
      whatsappConfig,
      examsConfig,
      managementLaboratorialExamsConfig,
      hasExamManagerProfile,
      healthProgramsConfig,
      ticketConfig,
      coparticipationConfig,
      irConfig,
      refundConfig,
      faqConfig,
      graceperiodConfig,
      getActionName,
      topMed,
      phone,
      onOpenHandler,
      doctorAgendaInstalled,
      doctorAgenda,
      medicalTransferInstalled,
      medicalTransferConfig,
      medicalTransferActionLabelIcon,
      medicalTransferActionLabelText,
      clickRepasseLoading,
      openClickRepasse,
    ],
  );

  return {
    items,
    open,
    onOpenHandler,
    onCloseHandler,
  };
};
